import type { NavigateFunction } from 'react-router-dom';
import { serviceUrls } from '@/config/serviceUrls';
import type { SchoolStudentFeatureKey } from '@/modules/platform/types/featureAccess';
import {
  handleSchoolFeatureDisabledIfPresent,
  SchoolFeatureDisabledHandledError,
} from '@/utils/schoolFeatureDisabledStudent';
import {
  AuthSessionExpiredHandledError,
  handleTutorSessionTokenAuthExpiredIfPresent,
} from '@/utils/tutorSessionTokenAuth';

const TOKEN_PRIMARY = '/v1/tutor/session-token';
const TOKEN_FALLBACK = '/v1/tutor/picture-talk-session-token';

function getGatewayTutorHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || '';
  const tenantId = localStorage.getItem('X_TENANT_ID') || '';
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  if (tenantId) h['x-tenant-id'] = tenantId;
  return h;
}

async function readTokenFromResponse(
  res: Response,
  navigate: NavigateFunction,
  refetchFeatureAccess: () => Promise<void>,
  fallbackFeatureKey: SchoolStudentFeatureKey | undefined
): Promise<string> {
  const txt = await res.text();
  if (!res.ok) {
    if (
      await handleSchoolFeatureDisabledIfPresent({
        status: res.status,
        bodyText: txt,
        navigate,
        refetchFeatureAccess,
        fallbackFeatureKey,
      })
    ) {
      throw new SchoolFeatureDisabledHandledError();
    }
    if (
      handleTutorSessionTokenAuthExpiredIfPresent({
        status: res.status,
        bodyText: txt,
        navigate,
      })
    ) {
      throw new AuthSessionExpiredHandledError();
    }
    throw new Error(`Token fetch failed: ${res.status} ${txt.slice(0, 200)}`);
  }
  let data: unknown;
  try {
    data = JSON.parse(txt) as unknown;
  } catch {
    throw new Error('Token response was not valid JSON.');
  }
  const o = data as Record<string, unknown>;
  const tok = o?.token;
  if (!tok || typeof tok !== 'string') throw new Error('Invalid token from backend.');
  return tok;
}

/**
 * Ephemeral Live API token. Tries primary tutor session token, then Picture Talk–specific route
 * (backend must implement fallback when primary rejects non–lesson callers).
 */
export async function fetchPictureTalkLiveToken(options: {
  navigate: NavigateFunction;
  refetchFeatureAccess: () => Promise<void>;
}): Promise<string> {
  const { navigate, refetchFeatureAccess } = options;
  const base = serviceUrls.gateway;
  const headers = getGatewayTutorHeaders();

  const tryUrl = async (path: string): Promise<string> => {
    const url = new URL(path, base).toString();
    const res = await fetch(url, { method: 'GET', headers });
    return readTokenFromResponse(res, navigate, refetchFeatureAccess, 'picture_talk');
  };

  try {
    return await tryUrl(TOKEN_PRIMARY);
  } catch (e) {
    if (e instanceof SchoolFeatureDisabledHandledError) throw e;
    if (e instanceof AuthSessionExpiredHandledError) throw e;
    try {
      return await tryUrl(TOKEN_FALLBACK);
    } catch (e2) {
      if (e2 instanceof SchoolFeatureDisabledHandledError) throw e2;
      if (e2 instanceof AuthSessionExpiredHandledError) throw e2;
      throw e;
    }
  }
}
